import BusinessCenter from '@mui/icons-material/BusinessCenter';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PeopleAltOutlined from '@mui/icons-material/PeopleAlt';

import { JOB_TITLES_TRANSLATIONS, JobTitleEnum } from '@pflegia/types/api/job-title';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';

import featuredJobCardHygieneImage from '@public/assets/weiterbildung/hygienefachkraft.png';
import featuredJobCardPdlImage from '@public/assets/weiterbildung/pdl.png';
import featuredJobCardQualityImage from '@public/assets/weiterbildung/qualitatsmanagement.png';
import featuredJobCardWblImage from '@public/assets/weiterbildung/wbl.png';

import { BLOG, BLOG_ARTICLE, GEHALT, JOBS } from '@shared/application/constants';

import type {
  FeaturedWeiterbildungJobCardDefinition,
  GehaltsentwicklungEntryDefinition,
  SalaryCalculatorFallbackReference,
  WeiterbildungPositionDefinition,
  WeiterbildungPositionLinks,
  WeiterbildungenMetricKey,
  WeiterbildungenPositionKey,
  WeiterbildungenSalaryKey,
  WeiterbildungenSalaryQueryConfigItem,
  WeiterbildungenTabDefinition,
} from './weiterbildungen.types';

export const GERMANY_SLUG = 'deutschland';
export const MAGAZIN_SECTION_LIMIT = 12;

export const buildWeiterbildungLinks = ({
  jobSlug,
  trainingPath,
}: {
  jobSlug: string;
  trainingPath: string;
}): WeiterbildungPositionLinks => ({
  jobs: `${JOBS}/${jobSlug}`,
  salary: `${GEHALT}/${jobSlug}/${GERMANY_SLUG}`,
  training: trainingPath,
});

export const WEITERBILDUNGEN_METRIC_KEYS: WeiterbildungenMetricKey[] = ['candidates', 'employers', 'free', 'trustpilot'];

export const METRIC_ICON_BY_KEY: Record<WeiterbildungenMetricKey, typeof PeopleAltOutlined | undefined> = {
  candidates: PeopleAltOutlined,
  employers: BusinessCenter,
  free: CheckCircle,
  trustpilot: undefined,
};

const buildTranslatedJobTitleLabel = ({ deputy, title }: SalaryCalculatorFallbackReference) => {
  const translatedJobTitle = JOB_TITLES_TRANSLATIONS[title];
  return deputy ? `${JOB_TITLES_TRANSLATIONS.deputy} ${translatedJobTitle}` : translatedJobTitle;
};

const buildJobTitleSlug = (jobTitle: SalaryCalculatorFallbackReference, slugOverride?: string) =>
  slugOverride ?? normalizeJobsParams(buildTranslatedJobTitleLabel(jobTitle));

const buildWeiterbildungPositionDefinition = (
  key: WeiterbildungenPositionKey,
  {
    furtherEducationPath,
    jobTitle,
    jobTitleSlugOverride,
    salaryCalculatorFallback,
    trainingPath,
  }: {
    furtherEducationPath?: string;
    jobTitle: SalaryCalculatorFallbackReference;
    jobTitleSlugOverride?: string;
    salaryCalculatorFallback: SalaryCalculatorFallbackReference;
    trainingPath: string;
  },
): WeiterbildungPositionDefinition => ({
  furtherEducationPath,
  jobTitle,
  jobTitleSlug: buildJobTitleSlug(jobTitle, jobTitleSlugOverride),
  key,
  salaryCalculatorFallback,
  trainingPath,
});

const WEITERBILDUNGEN_POSITION_DEFINITION_CONFIG: Record<
  WeiterbildungenPositionKey,
  {
    furtherEducationPath?: string;
    jobTitle: SalaryCalculatorFallbackReference;
    jobTitleSlugOverride?: string;
    salaryCalculatorFallback: SalaryCalculatorFallbackReference;
    trainingPath: string;
  }
> = {
  pflegedienstleitung: {
    furtherEducationPath: `${BLOG_ARTICLE}/pflegedienstleitung-pdl-weiterbildung`,
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.NursingSupervisor,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.NursingDirector,
    },
    trainingPath: `${BLOG_ARTICLE}/pflegedienstleitung-pdl-weiterbildung`,
  },
  wohnbereichsleitung: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.ResidentialSectorManager,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.ResidentialSectorManager,
    },
    trainingPath: `${BLOG_ARTICLE}/wohnbereichsleitung-wbl-weiterbildung`,
  },
  stationsleitung: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.UnitManager,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.UnitManager,
    },
    trainingPath: `${BLOG_ARTICLE}/stationsleitung-weiterbildung`,
  },
  ata: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.AnestheticAssistant,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.AnestheticAssistant,
    },
    trainingPath: `${BLOG_ARTICLE}/anasthesietechnische-r-assistent-in-ata-ausbildung-und-beruf`,
  },
  ota: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.OperationAssistant,
    },
    jobTitleSlugOverride: 'ota',
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.OperationAssistant,
    },
    trainingPath: `${BLOG}/op-schwester-ota-ausbildung-und-beruf`,
  },
  rettungssanitaeter: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.Paramedic,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.Paramedic,
    },
    trainingPath: `${BLOG_ARTICLE}/rettungssanitater-in-ausbildung-und-beruf`,
  },
  qualitaetsmanagement: {
    furtherEducationPath: `${BLOG_ARTICLE}/qualitatsmanagement-in-der-pflege-weiterbildung-als-karriereschritt`,
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.QualityManagement,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.QualityManagement,
    },
    trainingPath: `${BLOG_ARTICLE}/qualitaetsmanagement-pflege-weiterbildung`,
  },
  hygienefachkraft: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.HygieneSpecialist,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.HygieneSpecialist,
    },
    trainingPath: `${BLOG_ARTICLE}/hygienefachkraft-weiterbildung`,
  },
  praxisanleitung: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.Instructor,
    },
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.Instructor,
    },
    trainingPath: `${BLOG_ARTICLE}/praxisanleiter-pflege-weiterbildung`,
  },
};

export const WEITERBILDUNGEN_POSITION_DEFINITIONS = Object.fromEntries(
  Object.entries(WEITERBILDUNGEN_POSITION_DEFINITION_CONFIG).map(([key, config]) => [
    key,
    buildWeiterbildungPositionDefinition(key as WeiterbildungenPositionKey, config),
  ]),
) as Record<WeiterbildungenPositionKey, WeiterbildungPositionDefinition>;

export const WEITERBILDUNGEN_TAB_DEFINITIONS: WeiterbildungenTabDefinition[] = [
  {
    key: 'leadership',
    positions: ['pflegedienstleitung', 'wohnbereichsleitung', 'stationsleitung'],
  },
  {
    key: 'specialization',
    positions: ['ata', 'ota', 'rettungssanitaeter'],
  },
  {
    key: 'quality',
    positions: ['qualitaetsmanagement', 'hygienefachkraft', 'praxisanleitung'],
  },
];

export const WEITERBILDUNGEN_FEATURED_JOB_CARD_DEFINITIONS: FeaturedWeiterbildungJobCardDefinition[] = [
  {
    image: featuredJobCardPdlImage,
    positionKey: 'pflegedienstleitung',
  },
  {
    image: featuredJobCardWblImage,
    positionKey: 'wohnbereichsleitung',
  },
  {
    image: featuredJobCardQualityImage,
    positionKey: 'qualitaetsmanagement',
  },
  {
    image: featuredJobCardHygieneImage,
    positionKey: 'hygienefachkraft',
  },
];

export const WEITERBILDUNGEN_GEHALTSENTWICKLUNG_ENTRY_DEFINITIONS: GehaltsentwicklungEntryDefinition[] = [
  {
    key: 'pflegefachkraft',
  },
  {
    key: 'wohnbereichsleitung',
  },
  {
    key: 'pflegedienstleitung',
  },
];

const WEITERBILDUNGEN_POSITION_SALARY_QUERY_CONFIG = Object.fromEntries(
  Object.values(WEITERBILDUNGEN_POSITION_DEFINITIONS).map(position => [
    position.key,
    {
      jobTitle: position.jobTitle,
      jobTitleSlug: position.jobTitleSlug,
      salaryCalculatorFallback: position.salaryCalculatorFallback,
    },
  ]),
) as Record<WeiterbildungenPositionKey, WeiterbildungenSalaryQueryConfigItem>;

export const WEITERBILDUNGEN_SALARY_QUERY_CONFIG: Record<WeiterbildungenSalaryKey, WeiterbildungenSalaryQueryConfigItem> = {
  pflegefachkraft: {
    jobTitle: {
      deputy: false,
      title: JobTitleEnum.Nurse,
    },
    jobTitleSlug: buildJobTitleSlug({
      deputy: false,
      title: JobTitleEnum.Nurse,
    }),
    salaryCalculatorFallback: {
      deputy: false,
      title: JobTitleEnum.Nurse,
    },
  },
  ...WEITERBILDUNGEN_POSITION_SALARY_QUERY_CONFIG,
};

export const WEITERBILDUNGEN_QUESTIONS_AND_ANSWERS = [
  'whichNursingTrainingIsWorthIt',
  'howMuchDoesNursingTrainingCost',
  'howLongDoesNursingTrainingTake',
  'whichNursingTrainingPaysMost',
  'whichTrainingCanINursingAssistantDo',
  'whyContinuingEducationInNursingIsImportant',
];

export const WEITERBILDUNGEN_FAQ_LINK_URLS: Record<string, string> = {
  careAssistantLink: `${BLOG_ARTICLE}/pflegeassistent-in-ausbildung-und-beruf`,
  facilityManagerLink: `${BLOG_ARTICLE}/einrichtungsleitung-weiterbildung`,
  geriatricPsychiatryLink: `${BLOG_ARTICLE}/fachweiterbildung-gerontopsychiatrie`,
  infectionControlLink: `${BLOG_ARTICLE}/hygienefachkraft-weiterbildung`,
  infectionControlOfficerLink: `${BLOG_ARTICLE}/hygienebeauftragter-pflege-weiterbildung`,
  intensiveCareLink: `${BLOG_ARTICLE}/fachweiterbildung-anesthesie-und-intensivpflege`,
  jobsLink: JOBS,
  nursingAssistantLink: `${BLOG_ARTICLE}/pflegehelfer-in-ausbildung-und-beruf`,
  nursingServiceManagerLink: `${BLOG_ARTICLE}/pflegedienstleitung-pdl-weiterbildung`,
  oncologyNursingLink: `${BLOG_ARTICLE}/fachweiterbildung-onkologie`,
  palliativeCareLink: `${BLOG_ARTICLE}/palliative-care-weiterbildung`,
  practiceInstructorLink: `${BLOG_ARTICLE}/praxisanleiter-pflege-weiterbildung`,
  qualifiedNurseLink: `${BLOG_ARTICLE}/examinierte-pflegefachkraft-ausbildung-und-beruf`,
  qualityManagementLink: `${BLOG_ARTICLE}/qualitaetsmanagement-pflege-weiterbildung`,
  registeredNurseLink: `${BLOG_ARTICLE}/pflegefachfrau-pflegefachmann-ausbildung-und-berufsbild`,
  salaryArticlesLink: `${BLOG}?category=gehalt`,
  salaryPagesLink: `${BLOG}?category=gehalt`,
  salaryReportLink: `${BLOG_ARTICLE}/pflege-gehaltsreport-so-viel-verdienen-pflegekrafte-in-deutschland`,
  stationManagerLink: `${BLOG_ARTICLE}/stationsleitung-weiterbildung`,
  wardManagerLink: `${BLOG_ARTICLE}/wohnbereichsleitung-wbl-weiterbildung`,
  woundManagementLink: `${BLOG_ARTICLE}/wundmanagement-definition-und-einsatzbereiche`,
};
